import type { TreeInput } from '@goaltree/backend/convex/nodes'
import { CategoryBadge } from '@goaltree/ui/components/badge'
import { cn } from '@goaltree/ui/lib/utils'
import {
  Background,
  Controls,
  type Edge,
  Handle,
  MarkerType,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
} from '@xyflow/react'
import dagre from 'dagre'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import '@xyflow/react/dist/style.css'
import { NodeStatusIndicator } from './node-status-indicator'

const EFFORT_CLASSES: Record<string, string> = {
  low: 'text-green-500 dark:text-green-400',
  medium: 'text-yellow-500 dark:text-yellow-400',
  high: 'text-red-500 dark:text-red-400',
}

const DIMS = {
  goal: { w: 280, h: 88 },
  milestone: { w: 220, h: 68 },
  target: { w: 200, h: 60 },
  task: { w: 176, h: 72 },
} as const

function GoalNode({ data }: NodeProps) {
  const d = data as { title: string; category: string; taskCount: number }
  return (
    <NodeStatusIndicator status="initial">
      <div
        className="rounded-xl border-2 border-primary bg-card p-4 shadow-md"
        style={{ width: DIMS.goal.w }}
      >
        <div className="mb-2">
          <CategoryBadge category={d.category} />
        </div>
        <p className="text-sm font-semibold leading-snug">{d.title}</p>
        <div className="mt-2 flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
            <div className="h-full w-0 rounded-full bg-primary" />
          </div>
          <span className="text-xs text-muted-foreground">0/{d.taskCount}</span>
        </div>
        <Handle type="source" position={Position.Bottom} className="!border-primary !bg-primary" />
      </div>
    </NodeStatusIndicator>
  )
}

function MilestoneNode({ data }: NodeProps) {
  const d = data as { title: string; taskCount: number }
  return (
    <NodeStatusIndicator status="initial">
      <div
        className="rounded-lg border-2 border-border bg-card p-3 shadow-sm"
        style={{ width: DIMS.milestone.w }}
      >
        <Handle type="target" position={Position.Top} className="!bg-border" />
        <p className="text-sm font-medium leading-snug">{d.title}</p>
        <p className="mt-1 text-xs text-muted-foreground">0/{d.taskCount} tasks</p>
        <Handle type="source" position={Position.Bottom} className="!bg-border" />
      </div>
    </NodeStatusIndicator>
  )
}

function TargetNode({ data }: NodeProps) {
  const d = data as { title: string; taskCount: number }
  return (
    <NodeStatusIndicator status="initial">
      <div className="rounded-lg border bg-muted/40 p-3 shadow-sm" style={{ width: DIMS.target.w }}>
        <Handle type="target" position={Position.Top} className="!bg-border" />
        <p className="text-xs font-medium leading-snug text-foreground">{d.title}</p>
        <p className="mt-1 text-xs text-muted-foreground">0/{d.taskCount}</p>
        <Handle type="source" position={Position.Bottom} className="!bg-border" />
      </div>
    </NodeStatusIndicator>
  )
}

function TaskNode({ data }: NodeProps) {
  const { t } = useTranslation()
  const d = data as { title: string; effort: 'low' | 'medium' | 'high' }
  return (
    <NodeStatusIndicator status="initial">
      <div className="rounded-lg border bg-card p-3 shadow-sm" style={{ width: DIMS.task.w }}>
        <Handle type="target" position={Position.Top} className="!bg-border" />
        <div className="flex items-start gap-2">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 rounded-full border-2 border-muted-foreground/40" />
          <p className="flex-1 text-xs font-medium leading-snug">{d.title}</p>
        </div>
        <span
          className={cn(
            'mt-2 inline-block text-xs font-medium',
            EFFORT_CLASSES[d.effort] ?? EFFORT_CLASSES.medium,
          )}
        >
          {t(`stats.effort${d.effort.charAt(0).toUpperCase() + d.effort.slice(1)}`)}
        </span>
      </div>
    </NodeStatusIndicator>
  )
}

const nodeTypes = { goal: GoalNode, milestone: MilestoneNode, target: TargetNode, task: TaskNode }

const edgeDefaults = {
  type: 'smoothstep' as const,
  markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
}

function buildGraph(tree: TreeInput): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = []
  const edges: Edge[] = []

  const allTasks = tree.milestones.flatMap((ms) => ms.targets.flatMap((tg) => tg.tasks))
  nodes.push({
    id: 'goal',
    type: 'goal',
    position: { x: 0, y: 0 },
    data: { title: tree.title, category: tree.category, taskCount: allTasks.length },
  })

  tree.milestones.forEach((ms, mi) => {
    const msId = `ms-${mi}`
    const msTasks = ms.targets.flatMap((tg) => tg.tasks)
    nodes.push({
      id: msId,
      type: 'milestone',
      position: { x: 0, y: 0 },
      data: { title: ms.title, taskCount: msTasks.length },
    })
    edges.push({ id: `goal-${msId}`, source: 'goal', target: msId, ...edgeDefaults })

    ms.targets.forEach((tg, ti) => {
      const tgId = `ms-${mi}-tg-${ti}`
      nodes.push({
        id: tgId,
        type: 'target',
        position: { x: 0, y: 0 },
        data: { title: tg.title, taskCount: tg.tasks.length },
      })
      edges.push({ id: `${msId}-${tgId}`, source: msId, target: tgId, ...edgeDefaults })

      tg.tasks.forEach((task, tki) => {
        const taskId = `ms-${mi}-tg-${ti}-task-${tki}`
        nodes.push({
          id: taskId,
          type: 'task',
          position: { x: 0, y: 0 },
          data: { title: task.title, effort: task.effort },
        })
        edges.push({ id: `${tgId}-${taskId}`, source: tgId, target: taskId, ...edgeDefaults })
      })
    })
  })

  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({ rankdir: 'TB', nodesep: 24, ranksep: 64, marginx: 32, marginy: 32 })

  for (const node of nodes) {
    const dims = DIMS[node.type as keyof typeof DIMS] ?? DIMS.task
    g.setNode(node.id, { width: dims.w, height: dims.h })
  }
  for (const edge of edges) {
    g.setEdge(edge.source, edge.target)
  }

  dagre.layout(g)

  const laidOut = nodes.map((node) => {
    const pos = g.node(node.id)
    const dims = DIMS[node.type as keyof typeof DIMS] ?? DIMS.task
    return { ...node, position: { x: pos.x - dims.w / 2, y: pos.y - dims.h / 2 } }
  })

  return { nodes: laidOut, edges }
}

export default function HierarchyFlow({ tree }: { tree: TreeInput }) {
  const { nodes, edges } = useMemo(() => buildGraph(tree), [tree])

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      fitView
      fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
      minZoom={0.05}
      maxZoom={1.5}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      panOnScroll
      className="bg-background"
    >
      <Background />
      <Controls showInteractive={false} />
    </ReactFlow>
  )
}
